import React from 'react';
import { Calculator, Percent, DollarSign } from 'lucide-react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TaxCalculationPanel = ({
  subtotal,
  discountType,
  discountValue,
  discountAmount,
  taxAmount,
  total,
  onDiscountChange
}) => {
  const discountOptions = [
    { value: 'percentage', label: 'Percentage (%)' },
    { value: 'fixed', label: 'Fixed Amount ($)' }
  ];

  const handleDiscountTypeChange = (value) => {
    onDiscountChange(value, 0); // Reset discount value when type changes
  };

  const handleDiscountValueChange = (e) => {
    const value = parseFloat(e?.target?.value) || 0;
    onDiscountChange(discountType, value);
  };

  return (
    <div className="bg-white rounded-lg border">
      <div className="p-6 border-b">
        <div className="flex items-center gap-2">
          <Calculator className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Tax Calculation</h2>
        </div>
      </div>
      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Discount Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-foreground">Discount Settings</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Discount Type"
                options={discountOptions}
                value={discountType}
                onChange={handleDiscountTypeChange}
              />
              
              <Input
                label={discountType === 'percentage' ? 'Discount (%)' : 'Discount ($)'}
                type="number"
                value={discountValue}
                onChange={handleDiscountValueChange}
                min="0"
                step={discountType === 'percentage' ? '0.1' : '0.01'}
                max={discountType === 'percentage' ? '100' : undefined}
              />
            </div>

            {discountAmount > 0 && (
              <div className="p-3 bg-green-50 rounded-md border border-green-200">
                <div className="flex items-center gap-2">
                  <Percent className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-800">
                    Discount Applied: ${discountAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Tax Breakdown */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-foreground">Tax Breakdown</h3>
            
            <div className="space-y-3 p-4 bg-gray-50 rounded-md">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Subtotal (before discount)</span>
                <span className="font-medium">
                  ${subtotal?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
              
              {discountAmount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-green-600">Discount</span>
                  <span className="font-medium text-green-600">
                    -${discountAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Subtotal (after discount)</span>
                <span className="font-medium">
                  ${(subtotal - discountAmount)?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>

              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Total Tax</span>
                <span className="font-medium">
                  ${taxAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>

              <div className="pt-3 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-foreground">Total Amount</span>
                  <span className="text-xl font-bold text-primary">
                    ${total?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Subtotal</span>
            </div>
            <p className="text-lg font-bold text-blue-900 mt-1">
              ${subtotal?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>

          <div className="p-4 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2">
              <Percent className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-800">Discount</span>
            </div>
            <p className="text-lg font-bold text-green-900 mt-1">
              ${discountAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>

          <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
            <div className="flex items-center gap-2">
              <Calculator className="h-4 w-4 text-orange-600" />
              <span className="text-sm font-medium text-orange-800">Tax</span>
            </div>
            <p className="text-lg font-bold text-orange-900 mt-1">
              ${taxAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>

          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-800">Total</span>
            </div>
            <p className="text-xl font-bold text-purple-900 mt-1">
              ${total?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>
        </div>

        {/* Quick Discount Presets */}
        <div className="pt-4 border-t">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium text-foreground">Quick Discount:</span>
            {[5, 10, 15, 20]?.map(percent => (
              <button
                key={percent}
                onClick={() => onDiscountChange('percentage', percent)}
                className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
              >
                {percent}%
              </button>
            ))}
            <button
              onClick={() => onDiscountChange('percentage', 0)}
              className="px-2 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxCalculationPanel;